// @file Network requests related to Whiteboard Card Backgrounds from sandbox
/* eslint-disable no-return-await, no-template-curly-in-string */
import { asciiSafeStringify } from '@@/bits/json_stringify'
import type { VersionedFetchOptions } from '@@/surface/api/fetchable_object'
import FetchableObject from '@@/surface/api/fetchable_object'
import { fetchJson } from '@@/surface/api_fetch'
import type {
  WhiteboardCardBackground,
  WhiteboardCardBackgroundsApiResponse,
  WhiteboardDefaultFrameBackgroundApiResponse,
} from '@@/types'
import type { JsonAPIResource, JsonAPIResponse } from '@padlet/arvo'
import { HTTPMethod } from '@padlet/fetch'

class WhiteboardCardBackgroundsApi extends FetchableObject {
  public static readonly whiteboardCardBackgroundsUrl = `/api/\${ apiVersion }/whiteboard/backgrounds`
  public static readonly whiteboardDefaultCardBackgroundUrl = `/api/\${ apiVersion }/walls/\${ hashId }/whiteboard/default-frame-background`

  public static async fetch(
    fetchOptions: VersionedFetchOptions = { apiVersion: 8 },
  ): Promise<WhiteboardCardBackground[]> {
    const { apiVersion, ...restFetchOptions } = fetchOptions
    const response: JsonAPIResponse<WhiteboardCardBackgroundsApiResponse> = await fetchJson(
      this.buildUrl(this.whiteboardCardBackgroundsUrl, { apiVersion }),
      {
        method: HTTPMethod.get,
        ...restFetchOptions,
      },
    )
    return (response.data as JsonAPIResource<WhiteboardCardBackgroundsApiResponse>).attributes.backgrounds
  }

  public static async fetchDefaultBackground(
    hashId: string,
    fetchOptions: VersionedFetchOptions = { apiVersion: 9 },
  ): Promise<WhiteboardDefaultFrameBackgroundApiResponse> {
    const { apiVersion, ...restFetchOptions } = fetchOptions
    const response: JsonAPIResponse<WhiteboardDefaultFrameBackgroundApiResponse> = await fetchJson(
      this.buildUrl(this.whiteboardDefaultCardBackgroundUrl, { apiVersion, hashId }),
      {
        method: HTTPMethod.get,
        ...restFetchOptions,
      },
    )
    return (response.data as JsonAPIResource<WhiteboardDefaultFrameBackgroundApiResponse>).attributes
  }

  public static async updateDefaultBackground(
    payload: { hashId: string; backgroundType: string; backgroundUrl: string },
    fetchOptions: VersionedFetchOptions = { apiVersion: 9 },
  ): Promise<WhiteboardDefaultFrameBackgroundApiResponse> {
    const { apiVersion, ...restFetchOptions } = fetchOptions
    const response: JsonAPIResponse<WhiteboardDefaultFrameBackgroundApiResponse> = await fetchJson(
      this.buildUrl(this.whiteboardDefaultCardBackgroundUrl, { apiVersion, hashId: payload.hashId }),
      {
        ...restFetchOptions,
        method: HTTPMethod.patch,
        body: asciiSafeStringify({
          data: {
            attributes: {
              backgroundType: payload.backgroundType,
              backgroundUrl: payload.backgroundUrl,
            },
          },
        }),
      },
    )
    return (response.data as JsonAPIResource<WhiteboardDefaultFrameBackgroundApiResponse>).attributes
  }
}

export { WhiteboardCardBackgroundsApi }
